import { formatoNumero, formatoPorcentaje, formatoPrecio } from '@/lib/utils/formato-precio';
import { pisoEnTexto, techoEnTexto } from '../components/barra-de-rangos';
import type {
  Action,
  AlcanceDeLimite,
  ComoPide,
  PricingRule,
  RevisionDePrecio,
  TipoDeLimite,
} from '../types/pricing-rules.types';
import { dibujoDelPrecio, type DibujoDelPrecio } from './dibujo-del-precio';
import type { RecorridoDelPrecio } from './recorrido-del-precio';
import { alcanceEnFrase, describirRegla, esTodoElCatalogo } from './reglas-en-palabras';
import { nombreAutomatico, tieneNombrePropio } from './nueva-regla';
import { conLaAccion, cuerpoDelLimite, esSoloDeEsteProducto } from './limites-del-producto';

/**
 * «Este precio no cumple una regla» (lote B2, spec 246 punto 5; maqueta
 * #b2-regla-rota), sin pantalla: los tres bloques de arriba (lo que escribiste,
 * las reglas que no cumple, la estrategia que lo toma), el dibujo de la
 * explicación v2 con tu precio en la zona prohibida, y la excepción de D9
 * («Guardar X y ajustar los límites de este producto»).
 */

const fmt = formatoPrecio;
const CASI = 0.005;

/** El conflicto con la estrategia (lo arma `revisarAntesDeGuardar` con `explicar`). */
export interface EstrategiaQueLoToma {
  estrategia: { id: string; name: string };
  propone: number;
  /** De dónde sale lo que pide (spec 225). Ausente o null = no se sabe. */
  como?: ComoPide | null;
}

/** «Apuntar a un margen del 27 %», «Igualar al más barato (2.549,00)». */
export function metodoEnPalabras(c: ComoPide): string {
  switch (c.metodo) {
    case 'MARGIN_TARGET':
      return `Apuntar a un margen del ${formatoNumero(c.margen)} %`;
    case 'MATCH_LOWEST':
      return `Igualar al más barato (${fmt(c.masBarato)})`;
    case 'UNDERCUT_PERCENT':
      return `Quedar ${formatoNumero(c.porcentaje)} % debajo del más barato (${fmt(c.masBarato)})`;
    case 'DISCOUNT_PERCENT':
      return `Bajar ${formatoNumero(c.porcentaje)} % de tu precio de lista (${fmt(c.lista)})`;
  }
}

// Auditoría H9: «sólo este producto» sólo para una regla de exactamente su SKU;
// el escalón «producto» también tiene reglas de varios productos.
const ALCANCE: Record<AlcanceDeLimite, string> = {
  PRODUCTO: 'productos elegidos',
  MARCA_O_CATEGORIA: 'su marca o categoría',
  CATALOGO: 'todo el catálogo',
};

/** «a», «a y b», «a, b y c». */
export function enLista(xs: string[]): string {
  return xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`;
}

export interface BloqueDelAviso {
  clave: 'ESCRIBISTE' | 'REGLAS' | 'ESTRATEGIA';
  titulo: string;
  valor: string;
  texto: string;
  tono: 'neutro' | 'bloqueo';
}

/** Los tres bloques de arriba de la ventana. `hoy`: el precio guardado (de antes). */
export function bloquesDelAviso(args: {
  precio: number;
  hoy: number | null;
  revision: RevisionDePrecio | null;
  conflicto: EstrategiaQueLoToma | null;
  estrategiaSinRevisar: boolean;
  /** Auditoría H9: las reglas y el SKU, para decir «Sólo este producto» sólo cuando lo es. */
  reglas?: PricingRule[] | null;
  sku?: string | null;
}): BloqueDelAviso[] {
  const { precio, hoy, revision, conflicto } = args;
  const costo = revision?.costo ?? null;
  const partes: string[] = [];
  if (costo != null && precio < costo - CASI)
    partes.push(`Perdés ${fmt(Math.round((costo - precio) * 100) / 100)} por unidad.`);
  else if (revision?.margenDelPrecio != null)
    partes.push(`Ganás ${formatoNumero(revision.margenDelPrecio, 1)} %.`);
  if (hoy != null && hoy > 0 && Math.abs(precio - hoy) >= CASI) {
    const d = precio - hoy;
    partes.push(
      `${d < 0 ? 'Baja' : 'Sube'} ${fmt(Math.abs(d))} desde ${fmt(hoy)} (${formatoPorcentaje((d / hoy) * 100, { signo: true })}).`
    );
  }
  const escribiste: BloqueDelAviso = {
    clave: 'ESCRIBISTE',
    titulo: 'Lo que escribiste',
    valor: fmt(precio),
    texto: partes.join(' '),
    tono: 'neutro',
  };

  let reglas: BloqueDelAviso;
  const rompe = revision && !revision.cumple ? unicas(revision.rompe) : [];
  if (revision == null)
    reglas = {
      clave: 'REGLAS',
      titulo: 'Tus reglas',
      valor: 'No se pudieron revisar',
      texto: 'No se sabe si este precio cumple tus reglas.',
      tono: 'neutro',
    };
  else if (rompe.length === 0)
    reglas = {
      clave: 'REGLAS',
      titulo: 'Tus reglas',
      valor: 'Las cumple',
      texto: 'Ninguna regla lo frena.',
      tono: 'neutro',
    };
  else {
    const porId = new Map((args.reglas ?? []).map((r) => [r.id, r]));
    // De dónde viene: null = sólo de este producto; si no, a quién alcanza.
    const deDonde = (id: string): string | null => {
      const regla = porId.get(id);
      if (regla && args.sku && esSoloDeEsteProducto(regla.scope, args.sku)) return null;
      if (regla)
        return esTodoElCatalogo(regla.scope) ? 'todo el catálogo' : alcanceEnFrase(regla.scope);
      return ALCANCE[revision.rangos.find((x) => x.ruleId === id)?.alcance ?? 'CATALOGO'];
    };
    const deja = (r: (typeof rompe)[number]) =>
      r.min != null && precio < r.min
        ? `desde ${pisoEnTexto(r.min)}`
        : r.max != null
          ? `hasta ${techoEnTexto(r.max)}`
          : '';
    const [primera, ...otras] = rompe;
    const primeraDeDonde = deDonde(primera.ruleId);
    reglas = {
      clave: 'REGLAS',
      titulo: rompe.length === 1 ? 'La regla que no cumple' : 'Las reglas que no cumple',
      valor: primera.ruleName,
      texto: [
        `${primeraDeDonde == null ? 'Sólo este producto' : `«${primera.ruleName}», ${primeraDeDonde}`}: deja ${deja(primera)}.`,
        ...otras.map(
          (r) => `Y «${r.ruleName}» (${deDonde(r.ruleId) ?? 'sólo este producto'}) deja ${deja(r)}.`
        ),
      ].join(' '),
      tono: 'bloqueo',
    };
  }

  const estrategia: BloqueDelAviso = args.estrategiaSinRevisar
    ? {
        clave: 'ESTRATEGIA',
        titulo: 'La estrategia que lo toma',
        valor: 'No se pudo revisar',
        texto: 'No se sabe si una estrategia lo vuelve a cambiar.',
        tono: 'neutro',
      }
    : conflicto
      ? {
          clave: 'ESTRATEGIA',
          titulo: 'La estrategia que lo toma',
          valor: `«${conflicto.estrategia.name}»`,
          texto: `${conflicto.como ? `${metodoEnPalabras(conflicto.como)}. ` : ''}En su próxima corrida va a proponer ${fmt(conflicto.propone)}.`,
          tono: 'neutro',
        }
      : {
          clave: 'ESTRATEGIA',
          titulo: 'La estrategia que lo toma',
          valor: 'Ninguna lo cambia',
          texto: 'Ninguna estrategia lo vuelve a cambiar.',
          tono: 'neutro',
        };
  return [escribiste, reglas, estrategia];
}

function unicas<T extends { ruleId: string }>(xs: T[]): T[] {
  const vistos = new Set<string>();
  return xs.filter((x) => (vistos.has(x.ruleId) ? false : (vistos.add(x.ruleId), true)));
}

/**
 * El dibujo de la explicación v2 (spec 227) para un precio escrito a mano: lo
 * verde es lo que cumple tus reglas; «lo que escribiste» queda afuera; «pide la
 * estrategia», si hay una que lo toma. Sin flecha (nadie movió nada todavía).
 */
export function dibujoDelPrecioEscrito(args: {
  precio: number;
  hoy: number | null;
  revision: RevisionDePrecio;
  conflicto: EstrategiaQueLoToma | null;
}): DibujoDelPrecio | null {
  const { precio, hoy, revision, conflicto } = args;
  const rompe = new Set(revision.rompe.map((r) => r.ruleId));
  const r: RecorridoDelPrecio = {
    estado: revision.sinPrecioPosible ? 'SIN_PRECIO_POSIBLE' : 'CALCULADO',
    porQue: null,
    hoy,
    costo: revision.costo,
    pedido: conflicto
      ? {
          origen: 'ESTRATEGIA',
          estrategia: conflicto.estrategia.name,
          como: conflicto.como ?? null,
          usaMargenPropio: false,
          explicacionDeVuelta: null,
          precio: conflicto.propone,
        }
      : null,
    reglas: revision.rangos.map((x) => ({ ...x, manda: rompe.has(x.ruleId), probando: false })),
    pisadas: revision.pisados ?? [],
    cruce: revision.cruce,
    reglaQueLimito: null,
    redondeo: null,
    queda: precio,
    seSirve: null,
    salidas: null,
    elegida: null,
    comparacion: null,
    notas: [],
    mercado: null,
    masBarato: revision.masBarato ?? null,
    producto: null,
  };
  const d = dibujoDelPrecio(r, 'HOY');
  if (!d) return null;
  return {
    ...d,
    flecha: null,
    leyenda: d.leyenda.filter((l) => l.clave !== 'flecha'),
    marcas: d.marcas.map((m) =>
      m.clave === 'queda'
        ? { ...m, nombre: 'lo que escribiste' }
        : m.clave === 'pide'
          ? { ...m, nombre: 'pide la estrategia' }
          : m
    ),
  };
}

// ── La excepción (D9) ──────────────────────────────────────────────────────

export type AjusteDeLaExcepcion =
  | { tipo: 'EDITAR'; ruleId: string; accion: Action; texto: string }
  | { tipo: 'CREAR'; accion: Action; texto: string };

const TIPOS_QUE_SE_AJUSTAN: TipoDeLimite[] = [
  'PISO_FIJO',
  'TECHO_FIJO',
  'MARGEN_MINIMO',
  'SOBRE_EL_MAS_BARATO',
  'BAJO_EL_MAS_BARATO',
];

/** La acción de este producto que deja pasar justo este precio, o null si no se puede. */
function accionQueLoDeja(tipo: TipoDeLimite, precio: number, rev: RevisionDePrecio): Action | null {
  const mb = rev.masBarato ?? null;
  switch (tipo) {
    case 'PISO_FIJO':
      return { type: 'FORCE_FLOOR', value: precio };
    case 'TECHO_FIJO':
      return { type: 'FORCE_MAX_PRICE', value: precio };
    case 'MARGEN_MINIMO': {
      // Auditoría H1: con el margen exacto, no con `margenDelPrecio` (redondeado a
      // un decimal): 1.098,40 sobre 1.000 es 8,958 %, que redondeado da 9,0 y el
      // «No ganar menos de 9 %» lo seguía dejando afuera.
      const costo = rev.costo;
      if (costo == null || precio <= 0) return null;
      const m = ((precio - costo) / precio) * 100;
      if (m < 0) return null;
      return { type: 'FORCE_MIN_MARGIN', percent: Math.floor(m + 1e-9) };
    }
    case 'SOBRE_EL_MAS_BARATO': {
      if (mb == null || mb <= 0) return null;
      const p = Math.ceil((precio / mb - 1) * 100 - 1e-9);
      return p >= 0 && p <= 500 ? { type: 'MAX_ABOVE_COMPETITOR_MIN', percent: p } : null;
    }
    case 'BAJO_EL_MAS_BARATO': {
      if (mb == null || mb <= 0) return null;
      const p = Math.ceil((1 - precio / mb) * 100 - 1e-9);
      return p >= 0 && p <= 99 ? { type: 'MAX_BELOW_COMPETITOR_MIN', percent: p } : null;
    }
    default:
      return null;
  }
}

/**
 * D9: qué hay que cambiar para que este precio no rompa nada, sólo para este
 * producto. Por cada tipo que no cumple: si ya hay un límite sólo de este
 * producto, se edita; si no, se crea uno de este producto (y, como es más
 * específico, le gana al de su categoría o de todo el catálogo: la jerarquía).
 * null si alguno no se puede ajustar (un margen con el precio debajo del costo,
 * una regla vieja de margen máximo) o si el que no cumple es del escalón
 * «producto» pero de varios SKU (auditoría H3: uno nuevo de este producto queda
 * en el mismo escalón y decide el más exigente; editarlo cambiaría el límite de
 * los otros). `reglas` y `sku` dicen cuál es sólo de este producto; sin ellas,
 * uno del escalón «producto» no se puede ajustar.
 */
export function ajustesDeLaExcepcion(
  rev: RevisionDePrecio,
  precio: number,
  reglas: PricingRule[] | null = null,
  sku: string | null = null
): { ajustes: AjusteDeLaExcepcion[]; texto: string } | null {
  if (rev.cumple || rev.rompe.length === 0) return null;
  const alcanceDe = (id: string) => rev.rangos.find((x) => x.ruleId === id)?.alcance;
  const porId = new Map((reglas ?? []).map((r) => [r.id, r]));
  const soloEste = (id: string) => sku != null && esSoloDeEsteProducto(porId.get(id)?.scope, sku);
  const porTipo = new Map<TipoDeLimite, typeof rev.rompe>();
  for (const r of rev.rompe) porTipo.set(r.tipo, [...(porTipo.get(r.tipo) ?? []), r]);
  const ajustes: AjusteDeLaExcepcion[] = [];
  for (const [tipo, rotas] of porTipo) {
    if (!TIPOS_QUE_SE_AJUSTAN.includes(tipo)) return null;
    const accion = accionQueLoDeja(tipo, precio, rev);
    if (!accion) return null;
    if (rotas.some((r) => alcanceDe(r.ruleId) === 'PRODUCTO' && !soloEste(r.ruleId))) return null;
    // Ronda final B2: con dos límites propios del mismo tipo que no se cumplen,
    // se editan los dos (si no, «Así cumple todas tus reglas» mentiría).
    const propias = rotas.filter((r) => soloEste(r.ruleId));
    if (propias.length > 0) {
      for (const propia of propias) {
        const antes =
          tipo === 'PISO_FIJO' || tipo === 'TECHO_FIJO'
            ? `su límite de Bs ${fmt((tipo === 'PISO_FIJO' ? propia.min : propia.max) ?? precio)} ${tipo === 'PISO_FIJO' ? 'baja' : 'sube'} a ${fmt(precio)}`
            : `«${propia.ruleName}» pasa a «${describirRegla(accion)}»`;
        ajustes.push({ tipo: 'EDITAR', ruleId: propia.ruleId, accion, texto: antes });
      }
    } else
      ajustes.push({
        tipo: 'CREAR',
        accion,
        texto: `se crea «${describirRegla(accion)}» sólo para este producto`,
      });
  }
  // Primero lo que se edita, después lo que se crea (como la maqueta).
  ajustes.sort((a, b) => Number(a.tipo === 'CREAR') - Number(b.tipo === 'CREAR'));
  const unidas = enLista(ajustes.map((a) => a.texto));
  return {
    ajustes,
    texto: `${unidas.charAt(0).toUpperCase()}${unidas.slice(1)}. Así cumple todas tus reglas: los límites de este producto quedan en tu precio.`,
  };
}

/**
 * Los límites de la excepción no se pudieron cambiar (auditoría H10). `quedaron`:
 * lo que se cambió y no se pudo deshacer (vacío = no cambió nada).
 */
export class ErrorDeLaExcepcion extends Error {
  quedaron: string[];
  constructor(mensaje: string, quedaron: string[] = []) {
    super(mensaje);
    this.name = 'ErrorDeLaExcepcion';
    this.quedaron = quedaron;
  }
}

/** Vuelve los límites a como estaban; dice lo que no pudo volver. */
export type DeshacerAjustes = () => Promise<{ quedaron: string[] }>;

/**
 * Hace los cambios de la excepción con la API de Reglas. Primero lee y vuelve a
 * comprobar cada regla que se edita (sigue siendo sólo de este producto: H3);
 * después, una edición por regla, cambiando sólo la acción de ese tipo (el
 * redondeo u otro límite de la misma regla quedan: H2), y lo que se crea. Si el
 * nombre de una regla editada era el automático, se renombra con el límite
 * nuevo (si no, el nombre mentiría). Se crea encendida (sólo se ofrece con
 * permiso de crear, editar, encender y borrar: deshacer lo creado lo borra).
 *
 * Nada a medias (H10): si un paso falla, deshace los anteriores y avisa con
 * `ErrorDeLaExcepcion` (con lo que no pudo deshacer, si algo quedó). Devuelve
 * cómo deshacer todo, para cuando el precio después no se guarda.
 */
export async function aplicarAjustes(
  ajustes: AjusteDeLaExcepcion[],
  sku: string,
  api: {
    get: (id: string) => Promise<PricingRule>;
    update: (id: string, b: { actions: Action[]; name: string }) => Promise<unknown>;
    create: (b: ReturnType<typeof cuerpoDelLimite>) => Promise<{ id: string }>;
    delete: (id: string) => Promise<unknown>;
  }
): Promise<DeshacerAjustes> {
  const porRegla = new Map<string, Action[]>();
  for (const a of ajustes)
    if (a.tipo === 'EDITAR') porRegla.set(a.ruleId, [...(porRegla.get(a.ruleId) ?? []), a.accion]);
  const leidas = new Map<string, PricingRule>();
  for (const id of porRegla.keys()) {
    const r = await api.get(id);
    if (!esSoloDeEsteProducto(r.scope, sku))
      throw new ErrorDeLaExcepcion(
        `«${r.name}» ya no es sólo de este producto: no se cambió nada.`
      );
    leidas.set(id, r);
  }

  const pasos: Array<{
    texto: string;
    nombre: string;
    hacer: () => Promise<() => Promise<unknown>>;
  }> = [];
  for (const [id, acciones] of porRegla) {
    const r = leidas.get(id)!;
    const actions = acciones.reduce((xs, a) => conLaAccion(xs, a), r.actions);
    const name = tieneNombrePropio(r) ? r.name : nombreAutomatico(actions, r.scope ?? {});
    pasos.push({
      texto: `cambiar «${r.name}»`,
      // Ronda final B2: cada nombre con su verbo (lo que no vuelve se dice así).
      nombre: `se cambió «${r.name}»`,
      hacer: async () => {
        await api.update(id, { actions, name });
        return () => api.update(id, { actions: r.actions, name: r.name });
      },
    });
  }
  for (const a of ajustes)
    if (a.tipo === 'CREAR') {
      const cuerpo = cuerpoDelLimite(sku, a.accion, true);
      pasos.push({
        texto: `crear «${cuerpo.name}»`,
        nombre: `se creó «${cuerpo.name}»`,
        hacer: async () => {
          const creada = await api.create(cuerpo);
          return () => api.delete(creada.id);
        },
      });
    }

  const hechos: Array<{ nombre: string; deshacer: () => Promise<unknown> }> = [];
  const deshacer: DeshacerAjustes = async () => {
    const quedaron: string[] = [];
    for (const h of [...hechos].reverse()) {
      try {
        await h.deshacer();
      } catch {
        quedaron.unshift(h.nombre);
      }
    }
    return { quedaron };
  };
  for (const p of pasos) {
    try {
      hechos.push({ nombre: p.nombre, deshacer: await p.hacer() });
    } catch {
      const { quedaron } = await deshacer();
      throw quedaron.length > 0
        ? new ErrorDeLaExcepcion(`Quedó: ${enLista(quedaron)}; no se pudo ${p.texto}.`, quedaron)
        : new ErrorDeLaExcepcion(`No se pudo ${p.texto}: no se cambió nada.`);
    }
  }
  return deshacer;
}
